import { Boundaries } from "@planzone/core";
import { projectSynthesis } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { reduceMilestone } from "../../common/milestone";
import { reduceTask } from "../../common/task";

const initialState: State = [];

function handleRequest(
  state: State,
  { project }: Boundaries["retrieveProjectSynthesis"]["request"],
): State {
  const index = state.findIndex((res) => res.project.uuid === project.uuid);

  return index >= 0
    ? [
        ...state.slice(0, index),
        {
          request: setRequestStateToPending(),
          project,
          synthesis: projectSynthesis,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToPending(),
          project,
          synthesis: projectSynthesis,
        },
      ];
}

function handleSuccess(
  state: State,
  { project, synthesis }: Boundaries["retrieveProjectSynthesis"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? { ...res, request: setRequestStateToSuccess(), project, synthesis }
      : res,
  );
}

function handleFailure(
  state: State,
  { project }: Boundaries["retrieveProjectSynthesis"]["failure"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? { ...res, request: setRequestStateToFailure() }
      : res,
  );
}

function handleCreateTaskSucceeded(
  state: State,
  { task, list }: Boundaries["createTask"]["success"],
): State {
  return [];
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    synthesis: {
      ...req.synthesis,
      finishedMilestones: req.synthesis.finishedMilestones.map((milestone) =>
        reduceMilestone(milestone, event),
      ),
      delayedMilestones: req.synthesis.delayedMilestones.map((milestone) =>
        reduceMilestone(milestone, event),
      ),
      upComingMilestones: req.synthesis.upComingMilestones.map((milestone) =>
        reduceMilestone(milestone, event),
      ),
      delayedTasks: req.synthesis.delayedTasks.map((task) =>
        reduceTask(task, event),
      ),
      upComingTasks: req.synthesis.upComingTasks.map((task) =>
        reduceTask(task, event),
      ),
    },
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveProjectSynthesisRequested]: handleRequest,
      [events.retrieveProjectSynthesisSucceeded]: handleSuccess,
      [events.retrieveProjectSynthesisFailed]: handleFailure,
      [events.createTaskSucceeded]: handleCreateTaskSucceeded,
      [events.createKanbanTaskSucceeded]: handleCreateTaskSucceeded,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
